import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ChevronDown } from "lucide-react";

/**
 * ContentEffectCard — client-report chapter card showing Meta Business
 * Suite "Content Overview" metrics (organic + ads blended content
 * performance). Renders FB and IG side-by-side panels.
 *
 * Month-over-month treatment:
 *  - Each KPI gets a small inline delta pill vs the immediately prior
 *    uploaded month for the same platform.
 *  - Two callout strips above the panels: "What's working" and
 *    "Worth a conversation" — surface the top movers as a story.
 *  - Diplomatic tone throughout: declines render in muted slate/amber
 *    (NOT alarming red), and the negative callout is framed as a
 *    conversation starter, never a failure.
 *  - Period switcher dropdown lets the agency flip between any
 *    uploaded month.
 */

const fmt = (n) => {
  if (n == null || isNaN(n)) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 10_000) return (n / 1_000).toFixed(1) + "K";
  if (n >= 1_000) return n.toLocaleString();
  return String(Math.round(n));
};

const fmtPeriodLabel = (start, end) => {
  if (!start || !end) return "-";
  // YYYY-MM-DD parsing
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  try {
    const s = start.split("-");
    const e = end.split("-");
    const sM = months[parseInt(s[1], 10) - 1];
    const eM = months[parseInt(e[1], 10) - 1];
    if (s[0] === e[0] && s[1] === e[1]) return `${sM} ${s[0]}`;
    return `${sM} ${s[0]} – ${eM} ${e[0]}`;
  } catch { return `${start} – ${end}`; }
};

// Compute pct delta. Returns null when prior is 0/missing or sample too small
// to be meaningful (prevents "+100% (1→2 link clicks)" type noise).
const computeDelta = (current, prior, minSample = 10) => {
  if (current == null || prior == null) return null;
  if (prior < minSample && current < minSample) return null; // both too tiny
  if (prior === 0) return current > 0 ? { pct: null, direction: "up", isNew: true } : null;
  const pct = ((current - prior) / prior) * 100;
  if (Math.abs(pct) < 0.5) return { pct: 0, direction: "flat" };
  return { pct, direction: pct > 0 ? "up" : "down" };
};

// Inline delta pill next to KPI values. Up = lime, down = muted amber
// (NOT red). Flat = quiet slate.
const DeltaPill = ({ delta }) => {
  if (!delta) return null;
  if (delta.isNew) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums bg-lime-400/15 border border-lime-400/30 text-lime-300">
        NEW
      </span>
    );
  }
  if (delta.direction === "flat") {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold tabular-nums bg-slate-700/40 border border-slate-600/40 text-slate-400">
        flat
      </span>
    );
  }
  const up = delta.direction === "up";
  const cls = up
    ? "bg-lime-400/15 border-lime-400/30 text-lime-300"
    : "bg-amber-400/10 border-amber-400/25 text-amber-300";
  const arrow = up ? "▲" : "▼";
  const display = Math.abs(delta.pct) >= 100
    ? `${up ? "+" : "−"}${Math.round(Math.abs(delta.pct))}%`
    : `${up ? "+" : "−"}${Math.abs(delta.pct).toFixed(Math.abs(delta.pct) >= 10 ? 0 : 1)}%`;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums border ${cls}`}>
      <span className="text-[8px]">{arrow}</span>{display}
    </span>
  );
};

const PLATFORM_META = {
  facebook: {
    label: "Facebook",
    accent: "from-sky-500/15 to-sky-500/5",
    border: "border-sky-400/30",
    barOrg: "bg-sky-400",
    barAd: "bg-sky-200/70",
    pill: "text-sky-300 border-sky-400/40 bg-sky-500/10",
    icon: (<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>),
  },
  instagram: {
    label: "Instagram",
    accent: "from-fuchsia-500/15 to-pink-500/5",
    border: "border-fuchsia-400/30",
    barOrg: "bg-fuchsia-400",
    barAd: "bg-fuchsia-200/70",
    pill: "text-fuchsia-300 border-fuchsia-400/40 bg-fuchsia-500/10",
    icon: (<svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2zM12 0C8.74 0 8.33.01 7.05.07 5.77.13 4.9.33 4.14.63a5.91 5.91 0 0 0-2.14 1.39A5.91 5.91 0 0 0 .63 4.14C.33 4.9.13 5.77.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.28.26 2.15.56 2.91.31.79.73 1.46 1.39 2.14a5.91 5.91 0 0 0 2.14 1.39c.76.3 1.63.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.28-.06 2.15-.26 2.91-.56a5.91 5.91 0 0 0 2.14-1.39 5.91 5.91 0 0 0 1.39-2.14c.3-.76.5-1.63.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.28-.26-2.15-.56-2.91a5.91 5.91 0 0 0-1.39-2.14A5.91 5.91 0 0 0 19.86.63C19.1.33 18.23.13 16.95.07 15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1 0 18.16 12 6.16 6.16 0 0 0 12 5.84zm0 10.16a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm6.41-11.85a1.44 1.44 0 1 0 1.44 1.44 1.44 1.44 0 0 0-1.44-1.44z"/></svg>),
  },
};

// Human-readable label per metric key — used in the callouts.
const METRIC_LABELS = {
  total_views: "Views",
  reach: "Reach",
  interactions: "Interactions",
  profile_visits: "Profile Visits",
  new_follows: "New Follows",
  link_clicks: "Link Clicks",
  reels_views: "Reels Views",
  stories_views: "Stories Views",
};

// Minimum sample sizes per metric so we don't make callouts about
// statistically thin movements (e.g., "Follows +200% (1→3)").
const METRIC_MIN_SAMPLES = {
  total_views: 200,
  reach: 100,
  interactions: 20,
  profile_visits: 20,
  new_follows: 5,
  link_clicks: 10,
  reels_views: 100,
  stories_views: 50,
};

const Stat = ({ label, value, delta }) => (
  <div className="bg-slate-900/40 border border-slate-700/40 rounded-lg px-2.5 py-2">
    <p className="text-[9px] uppercase tracking-wide text-slate-400 font-bold">{label}</p>
    <div className="flex items-center gap-1.5 mt-0.5">
      <p className="text-white text-lg font-bold tabular-nums leading-tight">{fmt(value)}</p>
      <DeltaPill delta={delta} />
    </div>
  </div>
);

const FormatRow = ({ label, value, max, color, delta }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className="text-[11px] text-slate-300 w-16 flex-shrink-0">{label}</span>
      <div className="flex-1 h-2.5 rounded-full bg-slate-800/70 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex items-center gap-1 w-24 justify-end">
        <span className="text-[11px] text-white tabular-nums font-semibold">{fmt(value)}</span>
        <DeltaPill delta={delta} />
      </div>
    </div>
  );
};

const PlatformPanel = ({ record, prior }) => {
  const meta = PLATFORM_META[record.platform] || PLATFORM_META.facebook;
  const totalViews = record.total_views || 0;
  const organic = record.organic_views || 0;
  const ads = record.ad_views || 0;
  const organicPct = totalViews > 0 ? Math.round((organic / totalViews) * 100) : 0;
  const adsPct = totalViews > 0 ? Math.max(0, 100 - organicPct) : 0;
  const multiplier = organic > 0 ? (totalViews / organic) : null;

  const reelsViews = record.reels_views || 0;
  const storiesViews = record.stories_views || 0;
  const formatMax = Math.max(reelsViews, storiesViews, 1);
  const topPosts = (record.top_posts || []).slice(0, 3);

  // Helper to get delta for a field, returns null if no prior record
  const d = (field) => {
    if (!prior) return null;
    return computeDelta(record[field], prior[field], METRIC_MIN_SAMPLES[field] || 10);
  };

  return (
    <div
      className={`bg-gradient-to-br ${meta.accent} border ${meta.border} rounded-2xl p-5`}
      data-testid={`content-effect-panel-${record.platform}`}
    >
      <div className="flex items-center gap-2 mb-4 text-white">
        {meta.icon}
        <p className="font-bold text-base">{meta.label}</p>
      </div>

      {/* Hero */}
      <div className="mb-5">
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold mb-1">Total Views</p>
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="text-white text-4xl font-bold tabular-nums leading-none">{fmt(totalViews)}</p>
          <DeltaPill delta={d("total_views")} />
        </div>
        <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-300 tabular-nums">
          {organic > 0 && <span>Organic <span className="text-white font-semibold">{fmt(organic)}</span></span>}
          {ads > 0 && <span>Ads <span className="text-white font-semibold">{fmt(ads)}</span></span>}
          {record.reach > 0 && <span className="inline-flex items-center gap-1">Reach <span className="text-white font-semibold">{fmt(record.reach)}</span> <DeltaPill delta={d("reach")} /></span>}
        </div>
      </div>

      {/* Organic vs Ads bar */}
      {(organic > 0 || ads > 0) && totalViews > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between text-[11px] mb-1.5">
            <span className="text-slate-400">Organic vs Ads</span>
            {multiplier && multiplier >= 1.5 && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold ${meta.pill}`}>
                ~{multiplier.toFixed(1)}× amplified
              </span>
            )}
          </div>
          <div className="h-3 rounded-full overflow-hidden bg-slate-800/70 flex">
            <div className={`h-full ${meta.barOrg}`} style={{ width: `${organicPct}%` }} />
            <div className={`h-full ${meta.barAd}`} style={{ width: `${adsPct}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-slate-400 tabular-nums">
            <span>Organic {organicPct}%</span>
            <span>Ads {adsPct}%</span>
          </div>
        </div>
      )}

      {/* Format leaderboard */}
      {(reelsViews > 0 || storiesViews > 0) && (
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold mb-2">Top Formats</p>
          <FormatRow label="Reels" value={reelsViews} max={formatMax} color={meta.barOrg} delta={d("reels_views")} />
          <FormatRow label="Stories" value={storiesViews} max={formatMax} color={meta.barAd} delta={d("stories_views")} />
        </div>
      )}

      {/* Audience signals */}
      {(record.profile_visits > 0 || record.new_follows > 0 || record.link_clicks > 0 || record.interactions > 0) && (
        <div className="grid grid-cols-2 gap-2 mb-5">
          {record.interactions > 0 && <Stat label="Interactions" value={record.interactions} delta={d("interactions")} />}
          {record.profile_visits > 0 && <Stat label="Profile Visits" value={record.profile_visits} delta={d("profile_visits")} />}
          {record.new_follows > 0 && <Stat label="New Follows" value={record.new_follows} delta={d("new_follows")} />}
          {record.link_clicks > 0 && <Stat label="Link Clicks" value={record.link_clicks} delta={d("link_clicks")} />}
        </div>
      )}

      {/* Top posts */}
      {topPosts.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-[0.16em] text-slate-400 font-bold mb-2">Top Posts</p>
          <div className="space-y-1.5">
            {topPosts.map((p, i) => (
              <div key={i} className="bg-slate-900/40 border border-slate-700/40 rounded-lg p-2.5">
                <p className="text-white text-xs font-semibold leading-snug line-clamp-2">{p.title || "Untitled post"}</p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-slate-400 mt-1 tabular-nums">
                  {p.date && <span>{p.date}</span>}
                  {p.views > 0 && <span>👁 <span className="text-white">{fmt(p.views)}</span></span>}
                  {p.likes > 0 && <span>❤ <span className="text-white">{fmt(p.likes)}</span></span>}
                  {p.comments > 0 && <span>💬 <span className="text-white">{fmt(p.comments)}</span></span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Build the "What's working / Worth a conversation" callout pairs by
// scanning all metrics across both platforms, computing deltas, then
// picking the top 2 winners and top 2 dips (subject to min sample size).
const buildCallouts = (currentRecords, priorRecords) => {
  const byPlatformPrior = Object.fromEntries((priorRecords || []).map((r) => [r.platform, r]));
  const movers = [];
  for (const cur of currentRecords) {
    const prior = byPlatformPrior[cur.platform];
    if (!prior) continue;
    for (const key of Object.keys(METRIC_LABELS)) {
      const delta = computeDelta(cur[key], prior[key], METRIC_MIN_SAMPLES[key] || 10);
      if (!delta || delta.isNew || delta.direction === "flat") continue;
      if (delta.pct == null) continue;
      movers.push({
        platform: cur.platform,
        metric: key,
        label: METRIC_LABELS[key],
        pct: delta.pct,
        current: cur[key],
        prior: prior[key],
      });
    }
  }
  const ups = movers.filter((m) => m.pct > 0).sort((a, b) => b.pct - a.pct).slice(0, 3);
  const downs = movers.filter((m) => m.pct < 0).sort((a, b) => a.pct - b.pct).slice(0, 3);
  return { ups, downs };
};

const CalloutStrip = ({ ups, downs }) => {
  if ((ups.length + downs.length) === 0) return null;
  const platLabel = (p) => p === "facebook" ? "FB" : "IG";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
      {ups.length > 0 && (
        <div
          className="rounded-xl bg-lime-500/8 border border-lime-400/30 p-3.5"
          data-testid="content-effect-callout-working"
        >
          <p className="text-lime-300 text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5 flex items-center gap-1.5">
            <span className="text-base leading-none">🌱</span> What&apos;s Working
          </p>
          <ul className="space-y-1 text-[12px] text-slate-200 leading-snug">
            {ups.map((m, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-lime-400 font-bold">▲</span>
                <span>
                  <span className="text-white font-semibold">{m.label}</span>
                  <span className="text-slate-400"> ({platLabel(m.platform)})</span>
                  <span className="text-lime-300 font-bold ml-1.5 tabular-nums">+{Math.round(m.pct)}%</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {downs.length > 0 && (
        <div
          className="rounded-xl bg-amber-500/8 border border-amber-400/25 p-3.5"
          data-testid="content-effect-callout-conversation"
        >
          <p className="text-amber-300 text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5 flex items-center gap-1.5">
            <span className="text-base leading-none">💬</span> Worth a Conversation
          </p>
          <ul className="space-y-1 text-[12px] text-slate-200 leading-snug">
            {downs.map((m, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-amber-400 font-bold">▼</span>
                <span>
                  <span className="text-white font-semibold">{m.label}</span>
                  <span className="text-slate-400"> ({platLabel(m.platform)})</span>
                  <span className="text-amber-300 font-bold ml-1.5 tabular-nums">{Math.round(m.pct)}%</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="text-amber-200/70 text-[10px] italic mt-2 leading-snug">
            Not a problem, just a data point. Your ad rep can dig into the why.
          </p>
        </div>
      )}
    </div>
  );
};

export default function ContentEffectCard({ records = [], clientName = "Your brand" }) {
  // Group records into periods (one entry per report period, holding 1-2
  // platform records). Sorted newest first.
  const periods = useMemo(() => {
    const map = new Map();
    for (const r of records) {
      const key = `${r.report_start || ""}_${r.report_end || ""}`;
      if (!map.has(key)) map.set(key, { key, start: r.report_start, end: r.report_end, records: [] });
      map.get(key).records.push(r);
    }
    return Array.from(map.values()).sort((a, b) => (b.end || "").localeCompare(a.end || ""));
  }, [records]);

  const [selectedKey, setSelectedKey] = useState("");
  // Default collapsed — matches other channel sections in the report so the
  // report doesn't unfold to a wall of data on first open. Click the header
  // (or chevron) to expand.
  const [isOpen, setIsOpen] = useState(false);
  const selectedPeriod = periods.find((p) => p.key === selectedKey) || periods[0];

  // Prior period for delta comparisons (always the period immediately
  // older than the selected one, regardless of dropdown).
  const priorPeriod = useMemo(() => {
    if (!selectedPeriod) return null;
    const idx = periods.findIndex((p) => p.key === selectedPeriod.key);
    return idx >= 0 && idx < periods.length - 1 ? periods[idx + 1] : null;
  }, [periods, selectedPeriod]);

  if (!records || records.length === 0 || !selectedPeriod) return null;

  // Sort panels: Facebook first
  const sorted = [...selectedPeriod.records].sort((a, b) => {
    if (a.platform === b.platform) return 0;
    if (a.platform === "facebook") return -1;
    if (b.platform === "facebook") return 1;
    return 0;
  });

  const combinedViews = sorted.reduce((s, r) => s + (r.total_views || 0), 0);
  const { ups, downs } = priorPeriod ? buildCallouts(sorted, priorPeriod.records) : { ups: [], downs: [] };
  const periodLabel = fmtPeriodLabel(selectedPeriod.start, selectedPeriod.end);

  return (
    <section
      className="bg-gradient-to-br from-slate-900 via-slate-900/95 to-fuchsia-900/15 rounded-2xl p-5 md:p-6 border border-fuchsia-400/20 mb-8"
      data-testid="content-effect-section"
    >
      {/* Header (clickable toggle) — collapsed by default, mirrors the
          collapse pattern used by Google Ads, AppFront, CTV, etc. */}
      <div
        onClick={() => setIsOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIsOpen((v) => !v); } }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        data-testid="content-effect-toggle"
        className="flex items-center gap-3 cursor-pointer select-none group"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-fuchsia-300 text-[11px] font-bold uppercase tracking-[0.18em]">
            The Content Effect
          </p>
          <p className="text-white text-base md:text-lg font-bold leading-snug">
            {fmt(combinedViews)} views across {clientName}&apos;s social content.
          </p>
          <p className="text-slate-400 text-[11px] mt-0.5 tabular-nums">{periodLabel}</p>
          <p className="text-fuchsia-300/70 text-[11px] mt-0.5 group-hover:opacity-100 opacity-80 transition-opacity">
            {isOpen ? "Click to collapse" : "Click to expand"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="px-3 py-1 bg-fuchsia-500/20 text-fuchsia-300 text-xs font-medium rounded-full border border-fuchsia-400/30">
            {sorted.length} platform{sorted.length !== 1 ? "s" : ""}
          </span>
          <ChevronDown className={`w-5 h-5 text-fuchsia-300 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Expandable body */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content-effect-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-5">
              {/* Sub-headline + period switcher */}
              <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                <p className="text-slate-400 text-sm">
                  How {clientName}&apos;s posts performed across Facebook and Instagram, both organic and paid.
                </p>
                {periods.length > 1 && (
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] text-slate-400 uppercase tracking-wide font-bold">Period</label>
                    <select
                      value={selectedPeriod.key}
                      onChange={(e) => setSelectedKey(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-slate-800 border border-fuchsia-400/30 text-white text-sm rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-fuchsia-400/70 cursor-pointer"
                      data-testid="content-effect-period-select"
                    >
                      {periods.map((p) => (
                        <option key={p.key} value={p.key}>{fmtPeriodLabel(p.start, p.end)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Callouts — only when prior period exists */}
              {priorPeriod && (
                <>
                  <p className="text-[10px] text-slate-500 uppercase tracking-[0.16em] font-semibold mb-2">
                    vs. prior month ({fmtPeriodLabel(priorPeriod.start, priorPeriod.end)})
                  </p>
                  <CalloutStrip ups={ups} downs={downs} />
                </>
              )}

              {/* Side-by-side panels */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                {sorted.map((r) => {
                  const prior = priorPeriod
                    ? priorPeriod.records.find((pp) => pp.platform === r.platform) || null
                    : null;
                  return <PlatformPanel key={`${r.platform}-${r.report_start}`} record={r} prior={prior} />;
                })}
              </div>

              {/* Footnote */}
              <p
                className="mt-5 pt-4 border-t border-fuchsia-400/10 text-slate-400 text-[11px] italic leading-relaxed"
                data-testid="content-effect-footnote"
              >
                Content performance reflects how {clientName}&apos;s posts (organic and boosted) showed up
                on each platform. This is distinct from ad campaign performance reporting, which
                measures paid spend and conversions separately. For deeper context on what these
                numbers mean for your business, talk to your advertising rep.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
